import { Button, Form, Modal, Tabs, message } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ProjectDetails from "./ProjectDetails";
import ReportConfiguration from "./ReportConfiguration";
import UnitsConfiguration from "./UnitsConfiguration";
import { transformToApiPayload } from "../../helpers/createProject/project-creation";
import {
  createProject,
  resetProjectState,
} from "../../redux/data360Live_Project/action";
import { convertLengthValue } from "../../utils/unitConversion";

const { TabPane } = Tabs;

// Default state values
const DEFAULT_PROJECT_DETAILS = {
  projectName: "",
  projectNumber: "",
  clientName: "",
  technicianName: "",
  weldsPlanned: "",
  wallThickness: "",
  pipeDiameter: "",
  pipeMaterial: "",
  startDate: null,
  endDate: null,
};

const DEFAULT_WELDING_SYSTEM = {
  iwm: false,
  p600z: false,
  p625: false,
  m500: false,
};

const DEFAULT_REPORT_CONFIG = {
  current: true,
  volts: true,
  frequency: false,
  target: false,
  horizontal_bias: false,
  arc_impedance: false,
  travel_speed: true,
  wire_speed: true,
  heat: true,
  true_energy: false,
  oscillation_width: false,
  total_wire_consumed: false,
  summary_page: false,
  is_high_low_avg_required: false,
};

const DEFAULT_UNITS = {
  current: "A",
  volts: "V",
  travel_speed: "in/min",
  true_energy: "kJ/in",
  heat: "kJ/in",
  wire_speed: "in/min",
  oscillation_width: "in",
  frequency: "cyc/min",
  target: "A",
  horizontal_bias: "A",
  total_wire_consumed: "in",
  band_diameter: "in",
  wire_diameter: "in",
  logging_distance: "in",
  distance: "in",
  arc_impedance: "mOhm",
  wall_thickness: "in",
  pipe_diameter: "in",
};

const CreateProjectMain = ({
  isModalVisible,
  handleCancel,
  isEditMode,
  initialData,
}) => {
  const dispatch = useDispatch();
  const { loading, error, success } = useSelector(
    (state) => state.data360LiveProjectReducer
  );
  const reducerList = useSelector((state) => state);
  const [form] = Form.useForm();

  // State
  const [activeTab, setActiveTab] = useState("1");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projectDetails, setProjectDetails] = useState(DEFAULT_PROJECT_DETAILS);
  const [weldTagging, setWeldTagging] = useState("Manual");
  const [weldingSystem, setWeldingSystem] = useState(DEFAULT_WELDING_SYSTEM);
  const [numHeads, setNumHeads] = useState("4");
  const [numStations, setNumStations] = useState("1");
  const [showAvgValues, setShowAvgValues] = useState(true);
  const [summaryPage, setSummaryPage] = useState(false);
  const [reportConfig, setReportConfig] = useState(DEFAULT_REPORT_CONFIG);
  const [units, setUnits] = useState(DEFAULT_UNITS);

  // Reset form when modal closes
  const resetForm = () => {
    form.resetFields();
    setProjectDetails(DEFAULT_PROJECT_DETAILS);
    setWeldTagging("Manual");
    setWeldingSystem(DEFAULT_WELDING_SYSTEM);
    setNumHeads("4");
    setNumStations("1");
    setShowAvgValues(true);
    setSummaryPage(false);
    setReportConfig(DEFAULT_REPORT_CONFIG);
    setUnits(DEFAULT_UNITS);
  };

  // Initialize form for edit mode
  const initializeEditMode = () => {
    if (!isEditMode || !initialData?.projectInfo) return;

    const projectInfo = initialData.projectInfo;

    // Convert thickness and pipe diameter from inches to display units
    let convertedWallThickness = projectInfo.pipe_thickness?.toString() || "";
    let convertedPipeDiameter = projectInfo.pipe_diameter?.toString() || "";

    // Get current units for conversion
    const currentUnits = projectInfo.units ?
      (typeof projectInfo.units === "string" ? JSON.parse(projectInfo.units) : projectInfo.units) :
      DEFAULT_UNITS;

    // Convert from inches to display units
    if (convertedWallThickness && currentUnits.wall_thickness) {
      convertedWallThickness = convertLengthValue(convertedWallThickness, "in", currentUnits.wall_thickness);
    }
    if (convertedPipeDiameter && currentUnits.pipe_diameter) {
      convertedPipeDiameter = convertLengthValue(convertedPipeDiameter, "in", currentUnits.pipe_diameter);
    }

    // Set project details
    const newProjectDetails = {
      projectName: projectInfo.project_name || "",
      projectNumber: projectInfo.project_number || "",
      clientName: projectInfo.customer_name || "",
      technicianName: projectInfo.user_id || "",
      weldsPlanned: projectInfo.welds_planned?.toString() || "",
      wallThickness: convertedWallThickness,
      pipeDiameter: convertedPipeDiameter,
      pipeMaterial: projectInfo.pipe_material || "",
      startDate: projectInfo.start_date ? moment(projectInfo.start_date) : null,
      endDate: projectInfo.end_date ? moment(projectInfo.end_date) : null,
    };

    setProjectDetails(newProjectDetails);
    form.setFieldsValue(newProjectDetails);
    setWeldTagging(projectInfo.weldAssignmentType || "Manual");
    setNumHeads(String(projectInfo.num_heads_iwm || 4));
    setNumStations(String(projectInfo.num_stations || 1));

    // Parse welding systems safely
    try {
      const weldingSystemData = Array.isArray(projectInfo.welding_systems)
        ? {
            iwm: projectInfo.welding_systems.includes("iwm") || false,
            p600z: projectInfo.welding_systems.includes("p600z") || false,
            p625: projectInfo.welding_systems.includes("p625") || false,
            m500: projectInfo.welding_systems.includes("m500") || false,
          }
        : {};

      setWeldingSystem({
        iwm: !!weldingSystemData.iwm,
        p600z: !!weldingSystemData.p600z,
        p625: !!weldingSystemData.p625,
        m500: !!weldingSystemData.m500,
      });
    } catch (error) {
      console.error("Error parsing welding system data:", error);
      setWeldingSystem(DEFAULT_WELDING_SYSTEM);
    }

    // Parse report configuration safely
    if (projectInfo.report_columns) {
      try {
        const reportData =
          typeof projectInfo.report_columns === "string"
            ? JSON.parse(projectInfo.report_columns)
            : projectInfo.report_columns;

        setReportConfig(reportData);
        setSummaryPage(reportData.summary_page);
        setShowAvgValues(reportData.is_high_low_avg_required);
      } catch (error) {
        console.error("Error parsing report configuration:", error);
      }
    }
    if (projectInfo.units) {
      try {
        const unitsData =
          typeof projectInfo.units === "string"
            ? JSON.parse(projectInfo.units)
            : projectInfo.units;

        setUnits(unitsData);
      } catch (error) {
        console.error("Error parsing units data:", error);
      }
    }
  };

  // Check if project number exists
  const checkProjectNumberExists = (projectNumber) => {
    const existingProjects =
      reducerList?.data360LiveProjectReducer?.projectDetails?.projects || [];
    const isEditingCurrentProject =
      isEditMode && initialData?.projectInfo?.project_number === projectNumber;
    return (
      !isEditingCurrentProject &&
      existingProjects.some(
        (project) => project.project_number === projectNumber
      )
    );
  };

  // Validate required fields
  const validateRequiredFields = async () => {
    await form.validateFields([
      "projectName",
      "projectNumber",
      "clientName",
      "weldsPlanned",
      "startDate",
      "weldingSystem",
    ]);

    if (!Object.values(weldingSystem).some((value) => value)) {
      throw new Error("Please select at least one welding system");
    }

    // Validate IWM selection
    if (
      weldingSystem.iwm &&
      !(weldingSystem.p600z || weldingSystem.p625 || weldingSystem.m500)
    ) {
      throw new Error(
        "Please select either P600Z, P625 or M500 before proceeding."
      );
    }
  };

  // Submit form
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await validateRequiredFields();

      const formValues = await form.getFieldsValue();
      const projectNumber = formValues.projectNumber;

      // Double check project number uniqueness before submission
      if (checkProjectNumberExists(projectNumber)) {
        message.error(
          "Project Number already exists. Please use a unique project number."
        );
        setActiveTab("1"); // Go back to first tab
        return;
      }

      const formData = {
        projectDetails: {
          ...projectDetails,
          ...formValues,
          weldTagging,
          weldingSystem,
          numHeads,
          numStations,
        },
        reportConfiguration: {
          summaryPage,
          showAvgValues,
          reportConfig,
          units,
        },
      };

      const apiPayload = transformToApiPayload(formData);

      if (isEditMode && initialData?.projectInfo?.project_id) {
        dispatch({
          type: "UPDATE_PROJECT_REQUEST",
          payload: {
            projectId: initialData.projectInfo.project_id,
            projectData: apiPayload,
            currentProjectId: initialData.projectInfo.project_id,
          },
        });
      } else {
        dispatch(createProject(apiPayload));
      }
    } catch (error) {
      const errorMessage =
        error.message || "Please fill in all required fields";
      message.error(errorMessage);
      if (error.errorFields) {
        setActiveTab("1"); // Go back to first tab if validation fails
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle next/submit button click
  const handleNextClick = async () => {
    if (activeTab === "1") {
      try {
        await validateRequiredFields();
        setActiveTab("2");
      } catch (error) {
        message.error(error.message || "Please complete required fields");
      }
    } else if (activeTab === "2") {
      setActiveTab("3");
    } else {
      handleSubmit();
    }
  };

  // Handle previous button click
  const handlePreviousClick = () => {
    if (activeTab === "3") {
      setActiveTab("2");
    } else if (activeTab === "2") {
      setActiveTab("1");
    }
  };

  // Event handlers
  const handleProjectDetailsChange = (field, value) => {
    setProjectDetails((prev) => ({ ...prev, [field]: value }));
  };

  const handleWeldingSystemChange = (systemKey, checked) => {
    const newWeldingSystem = { ...weldingSystem, [systemKey]: checked };
    setWeldingSystem(newWeldingSystem);

    if (systemKey === "iwm" && !checked) {
      setNumHeads("4");
    }

    form.setFieldsValue({ weldingSystem: newWeldingSystem });
  };

  const handleReportConfigChange = (apiField, value) => {
    setReportConfig((prev) => ({ ...prev, [apiField]: value }));
    if (apiField === "summary_page") {
      setSummaryPage(value);
    }
  };

  const handleUnitConfigChange = (apiField, value) => {
    const newUnits = { ...units, [apiField]: value };
    setUnits(newUnits);

    // Convert thickness and pipe diameter values when their units change
    if (apiField === "wall_thickness" && projectDetails.wallThickness) {
      const convertedValue = convertLengthValue(
        projectDetails.wallThickness,
        units.wall_thickness,
        value
      );
      setProjectDetails((prev) => ({ ...prev, wallThickness: convertedValue }));
      form.setFieldsValue({ wallThickness: convertedValue });
    }

    if (apiField === "pipe_diameter" && projectDetails.pipeDiameter) {
      const convertedValue = convertLengthValue(
        projectDetails.pipeDiameter,
        units.pipe_diameter,
        value
      );
      setProjectDetails((prev) => ({ ...prev, pipeDiameter: convertedValue }));
      form.setFieldsValue({ pipeDiameter: convertedValue });
    }
  };

  // Effects
  useEffect(() => {
    if (!isModalVisible) resetForm();
  }, [isModalVisible]);

  useEffect(() => {
    initializeEditMode();
  }, [isEditMode, initialData]);

  // Synchronize summaryPage and showAvgValues with reportConfig
  useEffect(() => {
    setReportConfig((prev) => ({
      ...prev,
      summary_page: summaryPage,
      is_high_low_avg_required: showAvgValues,
    }));
  }, [summaryPage, showAvgValues]);

  useEffect(() => {
    if (success) {
      handleCancel();
      dispatch(resetProjectState());
      return;
    }

    if (error) {
      message.error(error || "Failed to create project");
    }
  }, [success, error, dispatch, handleCancel, isEditMode]);

  // Get button text based on current tab
  const getButtonText = () => {
    if (activeTab === "1") return "Next";
    if (activeTab === "2") return "Next";
    return isEditMode
      ? loading
        ? "Updating..."
        : "Update"
      : loading
      ? "Submitting..."
      : "Submit";
  };

  return (
    <Modal
      title={isEditMode ? "Edit Project" : "Add New Project"}
      open={isModalVisible}
      onCancel={handleCancel}
      footer={null}
      width={1300}
      centered
      className="add-project-modal"
      destroyOnClose={true}
      confirmLoading={isSubmitting}
    >
      <div className="add-project-modal-content">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          className="project-tabs"
        >
          <TabPane tab="Project Details" key="1">
            <ProjectDetails
              form={form}
              projectDetails={projectDetails}
              handleProjectDetailsChange={handleProjectDetailsChange}
              weldTagging={weldTagging}
              handleWeldTaggingChange={(e) => setWeldTagging(e.target.value)}
              weldingSystem={weldingSystem}
              handleWeldingSystemChange={handleWeldingSystemChange}
              numHeads={numHeads}
              handleNumHeadsChange={setNumHeads}
              numStations={numStations}
              handleNumStationsChange={setNumStations}
              isEditMode={isEditMode}
            />
          </TabPane>
          <TabPane tab="Units Configuration" key="2">
            <UnitsConfiguration
              units={units}
              handleUnitConfigChange={handleUnitConfigChange}
              resetToDefaultUnits={() => setUnits(DEFAULT_UNITS)}
              DEFAULT_UNITS={DEFAULT_UNITS}
            />
          </TabPane>
          <TabPane tab="Report Configuration" key="3">
            <ReportConfiguration
              summaryPage={summaryPage}
              handleSummaryPageChange={setSummaryPage}
              showAvgValues={showAvgValues}
              handleAvgValueChange={setShowAvgValues}
              reportConfig={reportConfig}
              handleReportConfigChange={handleReportConfigChange}
              resetToDefault={() => setReportConfig(DEFAULT_REPORT_CONFIG)}
              DEFAULT_REPORT_CONFIG={DEFAULT_REPORT_CONFIG}
            />
          </TabPane>
        </Tabs>

        <div className="modal-footer">
          {/* Left: Reset Button (only on tabs 2 and 3) */}
          <div className="modal-footer-left">
            {activeTab === "2" && (
              <Button
                className="reset-btn"
                onClick={() => setUnits(DEFAULT_UNITS)}
              >
                Reset Units To Default
              </Button>
            )}
            {activeTab === "3" && (
              <Button
                className="reset-btn"
                onClick={() => {
                  setReportConfig(DEFAULT_REPORT_CONFIG);
                  setSummaryPage(false);
                  setShowAvgValues(true);
                }}
              >
                Reset Configuration To Default
              </Button>
            )}
          </div>

          {/* Right: Navigation Buttons */}
          <div className="modal-footer-right">
            <Button onClick={handleCancel} className="cancel-btn">
              Cancel
            </Button>

            {activeTab !== "1" && (
              <Button onClick={handlePreviousClick} className="cancel-btn">
                Previous
              </Button>
            )}

            <Button
              type="primary"
              className="next-btn"
              onClick={handleNextClick}
              loading={loading || isSubmitting}
              disabled={loading || isSubmitting}
            >
              {getButtonText()}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreateProjectMain;
